import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    X, Download, Send, Flag, Clock, CheckCircle,
    Calendar, DollarSign, FileText, Shield, AlertTriangle,
    XCircle, Briefcase, Loader2, RefreshCw, FolderPlus, Receipt
} from 'lucide-react';
import { Contract, contractsService } from '../../services/contracts.service';
import { useContractsStore } from '../../store/contractsStore';
import AddProjectModal from '../projects/AddProjectModal';
import ComposeEmailModal from '../communication/ComposeEmailModal';

interface ViewContractModalProps {
    isOpen: boolean;
    onClose: () => void;
    contract: Contract;
}

const ViewContractModal: React.FC<ViewContractModalProps> = ({ isOpen, onClose, contract }) => {
    const navigate = useNavigate();
    const { updateDeliverable, addFlag, generateInvoiceForMilestone, generateFullContractInvoice, updateContractStatus, fetchContracts } = useContractsStore();
    const [activeTab, setActiveTab] = useState<'details' | 'deliverables' | 'compliance' | 'history' | 'finance'>('details');
    const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
    const [flagData, setFlagData] = useState<{ type: 'payment' | 'delivery' | 'compliance' | 'communication' | 'other'; description: string }>({ type: 'other', description: '' });

    // Guest link modal states
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);

    // Create project modal state
    const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);

    // Invoice generation states
    const [generatingInvoiceForMilestone, setGeneratingInvoiceForMilestone] = useState<string | null>(null);
    const [lastGeneratedInvoice, setLastGeneratedInvoice] = useState<{ invoice: any; milestoneId: string } | null>(null);
    const [contractInvoices, setContractInvoices] = useState<any[]>([]);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
    const [isGeneratingFullInvoice, setIsGeneratingFullInvoice] = useState(false);

    // Email modal state for signature requests
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [signatureEmailData, setSignatureEmailData] = useState<{
        to: string[];
        subject: string;
        body: string;
    } | null>(null);

    // Load invoices when Finance tab is opened
    useEffect(() => {
        if (activeTab === 'finance' && isOpen) {
            setIsLoadingInvoices(true);
            contractsService.getContractInvoices(contract.id)
                .then(invoices => setContractInvoices(invoices))
                .finally(() => setIsLoadingInvoices(false));
        }
    }, [activeTab, isOpen, contract.id]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const getStatusStyles = (status: string) => {
        const styles: Record<string, { bg: string; text: string }> = {
            draft: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
            sent: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
            negotiation: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
            signed: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400' },
            active: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
            completed: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
            terminated: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
        };
        return styles[status] || styles.draft;
    };

    const getDeliverableProgress = () => {
        if (!contract.deliverables || contract.deliverables.length === 0) return 0;
        const completed = contract.deliverables.filter(d => d.status === 'completed' || d.status === 'approved').length;
        return Math.round((completed / contract.deliverables.length) * 100);
    };

    const handleMarkDeliverableComplete = async (deliverableId: string) => {
        await updateDeliverable(contract.id, deliverableId, { status: 'completed' });
    };

    // ... (existing helpers)

    const handleSignContract = () => {
        onClose();
        navigate(`/dashboard/contracts/${contract.id}/sign`);
    };

    const handleSendContract = async () => {
        setIsGeneratingLink(true);
        try {
            const result = await contractsService.generateGuestLink(contract.id);
            const signatureLink = result.link;
            const expiryDate = new Date(result.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

            // Update contract status to 'sent' after generating the guest link
            if (contract.status === 'draft') {
                await updateContractStatus(contract.id, 'sent');
                // Refresh contracts list to update the UI
                fetchContracts();
            }

            // Prepare email content
            const emailSubject = `Contract for Signature: ${contract.title}`;
            const emailBody = `Hi${contract.partnership?.contact ? ` ${contract.partnership.contact.split(' ')[0]}` : ''},

I hope this message finds you well.

Please find the contract "${contract.title}" ready for your review and signature. You can access and sign the contract using the secure link below:

🔗 Signature Link: ${signatureLink}

This link will expire on ${expiryDate}.

If you have any questions or need any clarifications before signing, please don't hesitate to reach out.

Best regards`;

            // Set email data and open modal inline
            setSignatureEmailData({
                to: contract.partnership?.email ? [contract.partnership.email] : [],
                subject: emailSubject,
                body: emailBody,
            });
            setIsEmailModalOpen(true);
        } catch (error) {
            console.error('Failed to generate guest link:', error);
        } finally {
            setIsGeneratingLink(false);
        }
    };



    const handleAddFlag = async () => {
        if (flagData.description.trim()) {
            await addFlag(contract.id, flagData);
            setIsFlagModalOpen(false);
            setFlagData({ type: 'other', description: '' });
        }
    };

    const statusStyles = getStatusStyles(contract.status);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                    {/* Header */}
                    <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    {contract.title}
                                </h2>
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium capitalize ${statusStyles.bg} ${statusStyles.text}`}>
                                    {contract.status}
                                </span>
                            </div>
                            {contract.partnership?.brand && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                                    <Briefcase className="w-4 h-4" />
                                    {contract.partnership.brand}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        {contract.contractFileUrl && (
                            <a
                                href={contract.contractFileUrl}
                                download
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                Download
                            </a>
                        )}
                        {contract.status === 'draft' && (
                            <button
                                onClick={handleSendContract}
                                disabled={isGeneratingLink}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
                            >
                                {isGeneratingLink ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                                {isGeneratingLink ? 'Generating...' : 'Send for Signature'}
                            </button>
                        )}
                        {(contract.status === 'sent' || contract.status === 'negotiation' || contract.status === 'signed') && !contract.signatures?.creatorSignedAt && (
                            <button
                                onClick={handleSignContract}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                            >
                                <CheckCircle className="w-4 h-4" />
                                Sign Contract
                            </button>
                        )}
                        {(contract.status === 'active' || contract.status === 'completed') && (
                            <button
                                onClick={() => {
                                    onClose();
                                    navigate(`/dashboard/contracts/${contract.id}/sign?view=true`);
                                }}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-xl hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                            >
                                <FileText className="w-4 h-4" />
                                View Agreement
                            </button>
                        )}
                        <button
                            onClick={() => setIsFlagModalOpen(true)}
                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        >
                            <Flag className="w-4 h-4" />
                            Flag Issue
                        </button>
                        {/* Create Project - for active/signed contracts with deliverables */}
                        {['active', 'signed'].includes(contract.status) && contract.deliverables && contract.deliverables.length > 0 && (
                            <button
                                onClick={() => setIsCreateProjectModalOpen(true)}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                            >
                                <FolderPlus className="w-4 h-4" />
                                Create Project
                            </button>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center gap-1 px-6 py-2 border-b border-gray-200 dark:border-gray-700">
                        {(['details', 'finance', 'deliverables', 'compliance', 'history'] as const).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors capitalize ${activeTab === tab
                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[60vh]">
                        {/* Details Tab */}
                        {activeTab === 'details' && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Terms</h3>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                                                <DollarSign className="w-4 h-4" />
                                                <span className="text-xs font-medium uppercase">Total Value</span>
                                            </div>
                                            <p className="text-xl font-bold text-gray-900 dark:text-white">
                                                {formatCurrency(contract.totalValue)}
                                            </p>
                                        </div>

                                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                                                <Clock className="w-4 h-4" />
                                                <span className="text-xs font-medium uppercase">Duration</span>
                                            </div>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                {formatDate(contract.startDate)}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                to {formatDate(contract.endDate)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Payment Terms */}
                                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Payment Terms</h4>
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="px-2.5 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-lg text-sm font-medium capitalize">
                                                {contract.paymentTerms?.type || 'Milestone'}
                                            </span>
                                        </div>
                                        {contract.paymentTerms?.milestones && contract.paymentTerms.milestones.length > 0 && (
                                            <div className="space-y-2">
                                                {contract.paymentTerms.milestones.map((milestone, index) => {
                                                    const milestoneId = milestone.id || `milestone-${index}`;
                                                    const isGenerating = generatingInvoiceForMilestone === milestoneId;
                                                    const canGenerateInvoice = milestone.status === 'pending' && (contract.status === 'active' || contract.status === 'signed');
                                                    const canSendInvoice = milestone.status === 'invoiced' && lastGeneratedInvoice?.milestoneId === milestoneId;

                                                    const handleGenerateInvoice = async () => {
                                                        if (!milestoneId || isGenerating) return;
                                                        setGeneratingInvoiceForMilestone(milestoneId);
                                                        try {
                                                            const result = await generateInvoiceForMilestone(contract.id, milestoneId);
                                                            if (result) {
                                                                setLastGeneratedInvoice({ invoice: result.invoice, milestoneId });
                                                            }
                                                        } finally {
                                                            setGeneratingInvoiceForMilestone(null);
                                                        }
                                                    };

                                                    const handleSendInvoice = () => {
                                                        if (!lastGeneratedInvoice?.invoice) return;
                                                        const invoice = lastGeneratedInvoice.invoice;
                                                        const milestoneAmount = (contract.totalValue * milestone.percentage) / 100;
                                                        navigate('/communication', {
                                                            state: {
                                                                prefilledTo: [contract.partnership?.email || ''].filter(Boolean),
                                                                prefilledSubject: `Invoice ${invoice.invoiceNumber || ''} - ${milestone.name} (${contract.title})`,
                                                                prefilledBody: `Dear ${contract.partnership?.contact || 'Team'},\n\nPlease find below the invoice for the ${milestone.name} milestone (${milestone.percentage}% of contract value).\n\nAmount: ${formatCurrency(milestoneAmount)}\nDue Date: ${invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'As per contract terms'}\n\nPlease process the payment at your earliest convenience.\n\nBest regards`,
                                                            }
                                                        });
                                                    };

                                                    return (
                                                        <div key={index} className="flex items-center justify-between text-sm py-1.5">
                                                            <span className="text-gray-600 dark:text-gray-400">{milestone.name}</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium text-gray-900 dark:text-white">{milestone.percentage}%</span>
                                                                <span className={`w-2 h-2 rounded-full ${milestone.status === 'paid' ? 'bg-green-500' :
                                                                    milestone.status === 'invoiced' ? 'bg-yellow-500' : 'bg-gray-300'
                                                                    }`} />
                                                                {canGenerateInvoice && (
                                                                    <button
                                                                        onClick={handleGenerateInvoice}
                                                                        disabled={isGenerating}
                                                                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-50"
                                                                    >
                                                                        {isGenerating ? (
                                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                                        ) : (
                                                                            <Receipt className="w-3 h-3" />
                                                                        )}
                                                                        Invoice
                                                                    </button>
                                                                )}
                                                                {canSendInvoice && (
                                                                    <button
                                                                        onClick={handleSendInvoice}
                                                                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                                                    >
                                                                        <Send className="w-3 h-3" />
                                                                        Send
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {/* Signatures */}
                                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Signatures</h4>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600 dark:text-gray-400">Creator</span>
                                                {contract.signatures?.creatorSignedAt ? (
                                                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                                                        <CheckCircle className="w-4 h-4" />
                                                        Signed {formatDate(contract.signatures.creatorSignedAt)}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 dark:text-gray-500 text-sm">Pending</span>
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600 dark:text-gray-400">Brand</span>
                                                <div className="flex items-center gap-2">
                                                    {contract.signatures?.brandSignedAt ? (
                                                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                                                            <CheckCircle className="w-4 h-4" />
                                                            Signed {formatDate(contract.signatures.brandSignedAt)}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <span className="text-gray-400 dark:text-gray-500 text-sm">Pending</span>
                                                            {(contract.status === 'sent' || contract.status === 'negotiation' || contract.status === 'signed') && (
                                                                <button
                                                                    onClick={handleSendContract}
                                                                    disabled={isGeneratingLink}
                                                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
                                                                >
                                                                    {isGeneratingLink ? (
                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                    ) : (
                                                                        <RefreshCw className="w-3 h-3" />
                                                                    )}
                                                                    Resend
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Auto-Renewal & Termination */}
                                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">Auto-Renewal</span>
                                            <span className={`text-sm font-medium ${contract.autoRenewal ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}>
                                                {contract.autoRenewal ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">Termination Notice</span>
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                {contract.terminationNoticeDays} days
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column - Progress & Notes */}
                                <div className="space-y-4">
                                    {/* Deliverables Progress */}
                                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Deliverables Progress</h4>
                                        <div className="flex items-center gap-4 mb-2">
                                            <div className="flex-1 h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-primary-500 rounded-full transition-all"
                                                    style={{ width: `${getDeliverableProgress()}%` }}
                                                />
                                            </div>
                                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                {getDeliverableProgress()}%
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {contract.deliverables.filter(d => d.status === 'completed' || d.status === 'approved').length} of {contract.deliverables.length} completed
                                        </p>
                                    </div>

                                    {/* Tags */}
                                    {contract.tags && contract.tags.length > 0 && (
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Tags</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {contract.tags.map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="px-2.5 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-lg text-sm"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Notes */}
                                    {contract.notes && (
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Notes</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                                                {contract.notes}
                                            </p>
                                        </div>
                                    )}

                                    {/* Active Flags */}
                                    {contract.flags && contract.flags.filter(f => !f.resolvedAt).length > 0 && (
                                        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                                            <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
                                                <AlertTriangle className="w-4 h-4" />
                                                Active Issues
                                            </h4>
                                            <div className="space-y-2">
                                                {contract.flags.filter(f => !f.resolvedAt).map((flag) => (
                                                    <div key={flag.id} className="text-sm">
                                                        <span className="font-medium text-red-700 dark:text-red-400 capitalize">{flag.type}:</span>
                                                        <span className="text-red-600 dark:text-red-300 ml-1">{flag.description}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Deliverables Tab */}
                        {activeTab === 'deliverables' && (
                            <div className="space-y-4">
                                {contract.deliverables.length === 0 ? (
                                    <div className="text-center py-8">
                                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                        <p className="text-gray-500 dark:text-gray-400">No deliverables defined</p>
                                    </div>
                                ) : (
                                    contract.deliverables.map((deliverable) => {
                                        const isComplete = deliverable.status === 'completed' || deliverable.status === 'approved';
                                        return (
                                            <div
                                                key={deliverable.id}
                                                className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                                            >
                                                <button
                                                    onClick={() => deliverable.id && handleMarkDeliverableComplete(deliverable.id)}
                                                    disabled={isComplete}
                                                    className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isComplete
                                                        ? 'bg-green-500 border-green-500 text-white'
                                                        : 'border-gray-300 dark:border-gray-500 hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                                                        }`}
                                                >
                                                    {isComplete && <CheckCircle className="w-4 h-4" />}
                                                </button>
                                                <div className="flex-1">
                                                    <h4 className={`font-medium ${isComplete ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                                                        {deliverable.title}
                                                    </h4>
                                                    {deliverable.description && (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                            {deliverable.description}
                                                        </p>
                                                    )}
                                                    {deliverable.deadline && (
                                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 flex items-center gap-1">
                                                            <Calendar className="w-3 h-3" />
                                                            Due: {formatDate(deliverable.deadline)}
                                                        </p>
                                                    )}
                                                </div>
                                                <span className={`px-2.5 py-1 text-xs font-medium rounded-lg capitalize ${isComplete
                                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                    : deliverable.status === 'in_progress'
                                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                                        : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                                                    }`}>
                                                    {deliverable.status?.replace('_', ' ')}
                                                </span>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {/* Compliance Tab */}
                        {activeTab === 'compliance' && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Shield className="w-5 h-5 text-gray-400" />
                                                <span className="font-medium text-gray-900 dark:text-white">FTC Disclosure</span>
                                            </div>
                                            {contract.compliance?.ftcDisclosure ? (
                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                            ) : (
                                                <XCircle className="w-5 h-5 text-red-500" />
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            {contract.compliance?.ftcDisclosure ? 'Required' : 'Not Required'}
                                        </p>
                                    </div>

                                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <span className="font-medium text-gray-900 dark:text-white">Usage Rights</span>
                                        <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-2">
                                            {contract.compliance?.usageRightsYears || 0} <span className="text-sm font-normal">years</span>
                                        </p>
                                    </div>

                                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <span className="font-medium text-gray-900 dark:text-white">Exclusivity Period</span>
                                        <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-2">
                                            {contract.compliance?.exclusivityDays || 0} <span className="text-sm font-normal">days</span>
                                        </p>
                                    </div>

                                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                        <span className="font-medium text-gray-900 dark:text-white">Revisions Allowed</span>
                                        <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-2">
                                            {contract.compliance?.revisionsAllowed || 0}
                                        </p>
                                    </div>

                                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl md:col-span-2">
                                        <span className="font-medium text-gray-900 dark:text-white">Kill Fee</span>
                                        <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-2">
                                            {contract.compliance?.killFeePercentage || 0}%
                                            <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                                                ({formatCurrency((contract.totalValue * (contract.compliance?.killFeePercentage || 0)) / 100)})
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Finance Tab */}
                        {activeTab === 'finance' && (
                            <div className="space-y-6">
                                {/* Payment Overview Cards */}
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl">
                                        <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase">Contract Value</p>
                                        <p className="text-xl font-bold text-blue-700 dark:text-blue-300 mt-1">{formatCurrency(contract.totalValue)}</p>
                                    </div>
                                    <div className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 rounded-xl">
                                        <p className="text-xs font-medium text-yellow-600 dark:text-yellow-400 uppercase">Invoiced</p>
                                        <p className="text-xl font-bold text-yellow-700 dark:text-yellow-300 mt-1">
                                            {formatCurrency(contractInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0))}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl">
                                        <p className="text-xs font-medium text-green-600 dark:text-green-400 uppercase">Received</p>
                                        <p className="text-xl font-bold text-green-700 dark:text-green-300 mt-1">
                                            {formatCurrency(contractInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total || 0), 0))}
                                        </p>
                                    </div>
                                </div>

                                {/* Payment Terms & Invoice Generation */}
                                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">Invoice Generation</h4>
                                        <span className="px-2.5 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-lg text-xs font-medium capitalize">
                                            {contract.paymentTerms?.type || 'milestone'}
                                        </span>
                                    </div>

                                    {/* Milestone Type - Show individual milestones */}
                                    {(contract.paymentTerms?.type === 'milestone' || !contract.paymentTerms?.type) && contract.paymentTerms?.milestones && (
                                        <div className="space-y-2">
                                            {contract.paymentTerms.milestones.map((milestone, index) => {
                                                const milestoneId = milestone.id || `milestone-${index}`;
                                                const isGenerating = generatingInvoiceForMilestone === milestoneId;
                                                const canGenerateInvoice = milestone.status === 'pending' && ['active', 'signed'].includes(contract.status);
                                                const milestoneAmount = (contract.totalValue * milestone.percentage) / 100;

                                                const handleGenerate = async () => {
                                                    if (!milestoneId || isGenerating) return;
                                                    setGeneratingInvoiceForMilestone(milestoneId);
                                                    try {
                                                        const result = await generateInvoiceForMilestone(contract.id, milestoneId);
                                                        if (result) {
                                                            setLastGeneratedInvoice({ invoice: result.invoice, milestoneId });
                                                            setContractInvoices(prev => [result.invoice, ...prev]);
                                                        }
                                                    } finally {
                                                        setGeneratingInvoiceForMilestone(null);
                                                    }
                                                };

                                                return (
                                                    <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">{milestone.name}</p>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                {milestone.percentage}% • {formatCurrency(milestoneAmount)}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${milestone.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                                milestone.status === 'invoiced' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                                    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                                                                }`}>
                                                                {milestone.status}
                                                            </span>
                                                            {canGenerateInvoice && (
                                                                <button
                                                                    onClick={handleGenerate}
                                                                    disabled={isGenerating}
                                                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-50"
                                                                >
                                                                    {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Receipt className="w-3 h-3" />}
                                                                    Generate Invoice
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Non-milestone Types - Single invoice generation */}
                                    {contract.paymentTerms?.type && ['upfront', 'completion', 'net30', 'net60'].includes(contract.paymentTerms.type) && (
                                        <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {contract.paymentTerms.type === 'upfront' && 'Upfront Payment'}
                                                        {contract.paymentTerms.type === 'completion' && 'Payment on Completion'}
                                                        {contract.paymentTerms.type === 'net30' && 'Net 30 Days'}
                                                        {contract.paymentTerms.type === 'net60' && 'Net 60 Days'}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        Full contract value: {formatCurrency(contract.totalValue)}
                                                    </p>
                                                </div>
                                                {(() => {
                                                    const hasExistingInvoice = contractInvoices.some(inv => inv.type === contract.paymentTerms?.type);
                                                    const canGenerate = !hasExistingInvoice && (
                                                        (contract.paymentTerms?.type === 'completion' && contract.status === 'completed') ||
                                                        (['upfront', 'net30', 'net60'].includes(contract.paymentTerms?.type || '') && ['active', 'signed'].includes(contract.status))
                                                    );

                                                    const handleGenerateFull = async () => {
                                                        setIsGeneratingFullInvoice(true);
                                                        try {
                                                            const result = await generateFullContractInvoice(contract.id);
                                                            if (result) {
                                                                setContractInvoices(prev => [result.invoice, ...prev]);
                                                            }
                                                        } finally {
                                                            setIsGeneratingFullInvoice(false);
                                                        }
                                                    };

                                                    if (hasExistingInvoice) {
                                                        return <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full text-xs font-medium">Invoiced</span>;
                                                    }
                                                    if (canGenerate) {
                                                        return (
                                                            <button
                                                                onClick={handleGenerateFull}
                                                                disabled={isGeneratingFullInvoice}
                                                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-50"
                                                            >
                                                                {isGeneratingFullInvoice ? <Loader2 className="w-3 h-3 animate-spin" /> : <Receipt className="w-3 h-3" />}
                                                                Generate Invoice
                                                            </button>
                                                        );
                                                    }
                                                    return <span className="text-xs text-gray-500 dark:text-gray-400">Not eligible</span>;
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Invoice History */}
                                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Invoice History</h4>
                                    {isLoadingInvoices ? (
                                        <div className="flex items-center justify-center py-6">
                                            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                        </div>
                                    ) : contractInvoices.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No invoices generated yet</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {contractInvoices.map((invoice, index) => (
                                                <div key={invoice.id || index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{invoice.invoiceNumber || `Invoice #${index + 1}`}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {new Date(invoice.createdAt || invoice.issueDate).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(invoice.total || invoice.subtotal || 0)}</span>
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${invoice.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                            invoice.status === 'sent' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                                'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                                                            }`}>
                                                            {invoice.status}
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                navigate('/communication', {
                                                                    state: {
                                                                        prefilledTo: [contract.partnership?.email || ''].filter(Boolean),
                                                                        prefilledSubject: `Invoice ${invoice.invoiceNumber || ''} - ${contract.title}`,
                                                                        prefilledBody: `Dear ${contract.partnership?.contact || 'Team'},\n\nPlease find attached the invoice for ${contract.title}.\n\nAmount: ${formatCurrency(invoice.total || invoice.subtotal || 0)}\nDue Date: ${invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'As per terms'}\n\nPlease process the payment at your earliest convenience.\n\nBest regards`,
                                                                    }
                                                                });
                                                            }}
                                                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                                        >
                                                            <Send className="w-3 h-3" />
                                                            Send
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* History Tab */}
                        {activeTab === 'history' && (
                            <div className="space-y-6">
                                {/* Activity Timeline */}
                                {contract.activityLog && contract.activityLog.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Activity Timeline</h3>
                                        <div className="relative">
                                            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
                                            <div className="space-y-4">
                                                {contract.activityLog
                                                    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                                                    .map((activity, index) => {
                                                        const getActivityIcon = () => {
                                                            switch (activity.action) {
                                                                case 'created': return <FileText className="w-4 h-4" />;
                                                                case 'signed': return <CheckCircle className="w-4 h-4" />;
                                                                case 'status_change': return <RefreshCw className="w-4 h-4" />;
                                                                case 'sent': return <Send className="w-4 h-4" />;
                                                                case 'flag_added': return <Flag className="w-4 h-4" />;
                                                                case 'flag_resolved': return <CheckCircle className="w-4 h-4" />;
                                                                case 'deliverable_updated': return <CheckCircle className="w-4 h-4" />;
                                                                default: return <Clock className="w-4 h-4" />;
                                                            }
                                                        };
                                                        const getActivityColor = () => {
                                                            switch (activity.action) {
                                                                case 'created': return 'bg-blue-500';
                                                                case 'signed': return 'bg-green-500';
                                                                case 'status_change': return 'bg-purple-500';
                                                                case 'sent': return 'bg-indigo-500';
                                                                case 'flag_added': return 'bg-red-500';
                                                                case 'flag_resolved': return 'bg-emerald-500';
                                                                case 'deliverable_updated': return 'bg-cyan-500';
                                                                default: return 'bg-gray-500';
                                                            }
                                                        };
                                                        return (
                                                            <div key={activity.id || index} className="relative flex items-start gap-4 pl-10">
                                                                <div className={`absolute left-2 w-5 h-5 rounded-full ${getActivityColor()} text-white flex items-center justify-center`}>
                                                                    {getActivityIcon()}
                                                                </div>
                                                                <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                                                                    <div className="flex items-center justify-between mb-1">
                                                                        <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                                                            {activity.action.replace('_', ' ')}
                                                                        </span>
                                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                            {formatDate(activity.timestamp)}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{activity.description}</p>
                                                                    {activity.performedBy && (
                                                                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">By: {activity.performedBy}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Amendments */}
                                {contract.amendments && contract.amendments.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Amendments</h3>
                                        <div className="space-y-3">
                                            {contract.amendments.map((amendment) => (
                                                <div key={amendment.id} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="font-medium text-gray-900 dark:text-white">Version {amendment.version}</span>
                                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                                            {formatDate(amendment.amendedAt)}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{amendment.changes}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Resolved Flags */}
                                {contract.flags && contract.flags.filter(f => f.resolvedAt).length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Resolved Issues</h3>
                                        <div className="space-y-3">
                                            {contract.flags.filter(f => f.resolvedAt).map((flag) => (
                                                <div key={flag.id} className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="font-medium text-green-700 dark:text-green-400 capitalize">{flag.type}</span>
                                                        <span className="text-sm text-green-600 dark:text-green-500">
                                                            Resolved {flag.resolvedAt && formatDate(flag.resolvedAt)}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-green-600 dark:text-green-400">{flag.description}</p>
                                                    {flag.resolution && (
                                                        <p className="text-sm text-green-700 dark:text-green-300 mt-2 font-medium">
                                                            Resolution: {flag.resolution}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {(!contract.activityLog || contract.activityLog.length === 0) &&
                                    (!contract.amendments || contract.amendments.length === 0) &&
                                    (!contract.flags || contract.flags.filter(f => f.resolvedAt).length === 0) && (
                                        <div className="text-center py-8">
                                            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                            <p className="text-gray-500 dark:text-gray-400">No history yet</p>
                                            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                                Activity will appear here as you work with this contract
                                            </p>
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Flag Issue Modal */}
            {isFlagModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setIsFlagModalOpen(false)} />
                    <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Flag an Issue</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Issue Type
                                </label>
                                <select
                                    value={flagData.type}
                                    onChange={(e) => setFlagData(prev => ({ ...prev, type: e.target.value as 'payment' | 'delivery' | 'compliance' | 'communication' | 'other' }))}
                                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white"
                                >
                                    <option value="payment">Payment Issue</option>
                                    <option value="delivery">Delivery Issue</option>
                                    <option value="compliance">Compliance Issue</option>
                                    <option value="communication">Communication Issue</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={flagData.description}
                                    onChange={(e) => setFlagData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Describe the issue..."
                                    rows={4}
                                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setIsFlagModalOpen(false)}
                                    className="flex-1 px-4 py-2.5 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddFlag}
                                    disabled={!flagData.description.trim()}
                                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                                >
                                    Submit Flag
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Project Modal */}
            <AddProjectModal
                isOpen={isCreateProjectModalOpen}
                onClose={() => setIsCreateProjectModalOpen(false)}
                preselectedContractId={contract.id}
            />

            {/* ComposeEmailModal for signature requests */}
            <ComposeEmailModal
                isOpen={isEmailModalOpen}
                onClose={() => {
                    setIsEmailModalOpen(false);
                    setSignatureEmailData(null);
                }}
                preselectedTemplate="builtin-signature-template"
                {...(signatureEmailData?.to && { prefilledTo: signatureEmailData.to })}
                {...(signatureEmailData?.subject && { prefilledSubject: signatureEmailData.subject })}
                {...(signatureEmailData?.body && { prefilledBody: signatureEmailData.body })}
            />

        </div>
    );
};

export default ViewContractModal;
